import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./Icon";
import { cx, Input } from "./ui";

type Props = {
  value: string;
  /** Called with the chosen value: an existing option (its own spelling) or new text. */
  onChange: (value: string) => void;
  options: string[];
  /** Offer "Create “…”" for text that isn't an option yet. */
  allowCreate?: boolean;
  /**
   * Replaces the "Create" row, or hides it with null. Nothing is highlighted until the arrow keys move, so Enter keeps
   * the typed text rather than the closest option.
   */
  createRow?: (query: string) => { label: string; hint?: string } | null;
  /** For options loaded from the server: the text being typed. */
  onQueryChange?: (query: string) => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  id?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
};

/**
 * Pick from a list or type a new value. Typing filters the options; Enter takes the highlighted one (or creates);
 * leaving the field keeps what was typed, matched to an existing option's spelling when it's the same name.
 */
export function Combobox({
  value,
  onChange,
  options,
  allowCreate,
  createRow,
  onQueryChange,
  placeholder,
  maxLength = 100,
  className,
  ...aria
}: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const start = createRow ? -1 : 0;
  const [active, setActive] = useState(start);
  const listId = useId();
  const ref = useRef<HTMLDivElement>(null);

  // Follow outside changes (e.g. the saved value coming back from the server).
  useEffect(() => setText(value), [value]);

  const query = text.trim();
  const lower = query.toLowerCase();
  // The current value counts as an option even before the list catches up (e.g. a just-created one).
  const all = value && !options.some((o) => o.toLowerCase() === value.toLowerCase()) ? [...options, value] : options;
  const matches = all.filter((o) => o.toLowerCase().includes(lower));
  const exact = all.find((o) => o.toLowerCase() === lower);
  const canCreate = !!allowCreate && !!query && !exact;
  const custom = canCreate ? createRow?.(query) : undefined;
  const items: { label: string; value: string; create?: boolean; hint?: string }[] = [
    ...matches.map((o) => ({ label: o, value: o })),
    ...(canCreate && custom !== null ? [{ label: custom?.label ?? t("combobox.create", { value: query }), hint: custom?.hint, value: query, create: true }] : []),
  ];

  const commit = (next: string) => {
    setText(next);
    setOpen(false);
    if (next !== value) onChange(next);
  };
  // Leaving the field: the same name as an option takes its spelling; new text stays only if creating is allowed.
  const settle = () => commit(exact ?? (allowCreate || !query ? query : value));

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) return setOpen(true);
      const n = items.length;
      if (n) setActive((i) => (e.key === "ArrowUp" && i < 0 ? n - 1 : (i + (e.key === "ArrowDown" ? 1 : -1) + n) % n));
    } else if (e.key === "Enter" && open) {
      e.preventDefault();
      const item = items[active];
      commit(item ? item.value : (exact ?? (allowCreate ? query : value)));
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setText(value);
      setOpen(false);
    }
  };

  return (
    <div
      ref={ref}
      className={cx("relative", className)}
      onBlur={(e) => {
        if (!ref.current?.contains(e.relatedTarget as Node)) settle();
      }}
    >
      <Input
        {...aria}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && items[active] ? `${listId}-${active}` : undefined}
        value={text}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setText(e.target.value);
          setActive(start);
          setOpen(true);
          onQueryChange?.(e.target.value);
        }}
        onKeyDown={onKey}
        className="pr-8"
      />
      {text && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={t("common.clear")}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => commit("")}
          className="absolute top-1/2 right-1.5 -translate-y-1/2 rounded p-1 text-muted hover:text-text"
        >
          <Icon name="x" size={13} />
        </button>
      )}
      {open && items.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute top-full right-0 left-0 z-30 m-0 mt-1 max-h-60 list-none overflow-y-auto rounded-[var(--radius-card)] border border-border bg-surface p-1 shadow-[var(--shadow)]"
        >
          {items.map((item, i) => (
            <li
              key={`${item.create ? "+" : ""}${item.value}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              // mousedown, so the input doesn't blur (and settle) first.
              onMouseDown={(e) => {
                e.preventDefault();
                commit(item.value);
              }}
              onMouseEnter={() => setActive(i)}
              className={cx(
                "flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm",
                i === active && "bg-surface-2",
                item.create ? "font-medium text-accent" : item.value === value ? "font-semibold" : "text-text",
              )}
            >
              {item.create && !createRow && <Icon name="plus" size={13} />}
              {item.hint ? (
                <span className="flex flex-col">
                  {item.label}
                  <span className="text-xs font-normal text-muted">{item.hint}</span>
                </span>
              ) : (
                item.label
              )}
              {!item.create && item.value === value && <Icon name="check" size={13} className="ml-auto text-accent" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
